import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { useConnection } from 'hooks/useConnection'
import { useReadContractMultiple } from 'hooks/useReadContractMultiple'
import { useCallback, useMemo } from 'react'
import { type Address, formatUnits, getAddress } from 'viem'

import KodiakFarmAbi from '../../abis/KodiakFarm.json'
import RewardVaultAbi from '../../abis/RewardVault.json'
import { ApiFarm, ApiVaultData, FarmPosition, KodiakFarmStakedPosition, RewardToken } from '../../types/portfolio'
import { useMasterVaultsData } from './useMasterData'

export function useUserFarmPositions(options?: { enabled?: boolean }) {
  const { account: userAccount } = useConnection()
  const { data: vaults, ...queryResult } = useMasterVaultsData({
    enabled: options?.enabled,
  })

  const userVaultsWithFarms: ApiVaultData[] = useMemo(
    () => vaults?.filter((vault) => parseFloat(vault?.farmBalanceUSD?.toString() || '0') > 0) || [],
    [vaults]
  )

  const allUserFarms: ApiFarm[] = useMemo(
    () => userVaultsWithFarms.flatMap((vault) => (vault?.farm ? [vault.farm as ApiFarm] : [])),
    [userVaultsWithFarms]
  )

  const userKodiakFarms: ApiFarm[] = useMemo(
    () => allUserFarms.filter((farm) => farm.provider === 'kodiak') || [],
    [allUserFarms]
  )

  const userRewardVaultFarms: ApiFarm[] = useMemo(
    () => allUserFarms.filter((farm) => farm.provider === 'bgt') || [],
    [allUserFarms]
  )
  const contractCalls = useMemo(
    () =>
      (userAccount &&
        userKodiakFarms.map((farm) => ({
          address: getAddress(farm.id),
          abi: KodiakFarmAbi,
          functionName: 'lockedStakesOf',
          args: [userAccount as Address],
        }))) ||
      [],
    [userKodiakFarms, userAccount]
  )

  const {
    data: kodiakStakingResults,
    isLoading: isLoadingStakesData,
    error: errorStakesData,
    refetch: refetchKodiakStakesData,
  } = useReadContractMultiple<KodiakFarmStakedPosition[]>({
    contracts: contractCalls as any,
    allowFailure: true,
    query: {
      ...PORTFOLIO_REFETCH_POLICY,
      enabled: userAccount && userKodiakFarms.length > 0 && options?.enabled !== false,
    },
  })

  const rewardVaultBalanceContractCalls = useMemo(() => {
    if (!userAccount || !userRewardVaultFarms.length) return []

    return userRewardVaultFarms.map((farm) => ({
      address: getAddress(farm.id),
      abi: RewardVaultAbi,
      functionName: 'balanceOf',
      args: [userAccount as Address],
    }))
  }, [userRewardVaultFarms, userAccount])

  const {
    data: rewardVaultBalanceResults,
    isLoading: isLoadingRewardVaultBalances,
    error: errorRewardVaultBalances,
    refetch: refetchRewardVaultBalanceData,
  } = useReadContractMultiple({
    contracts: rewardVaultBalanceContractCalls as any,
    allowFailure: true,
    query: {
      ...PORTFOLIO_REFETCH_POLICY,
      enabled: userAccount && userRewardVaultFarms.length > 0 && options?.enabled !== false,
    },
  })

  const rewardVaultEarnedContractCalls = useMemo(() => {
    if (!userAccount || !userRewardVaultFarms.length) return []

    return userRewardVaultFarms.map((farm) => ({
      address: getAddress(farm.id),
      abi: RewardVaultAbi,
      functionName: 'earned',
      args: [userAccount as Address],
    }))
  }, [userRewardVaultFarms, userAccount])

  const {
    data: rewardVaultEarnedResults,
    isLoading: isLoadingRewardVaultEarned,
    error: errorRewardVaultEarned,
    refetch: refetchRewardVaultEarnedData,
  } = useReadContractMultiple({
    contracts: rewardVaultEarnedContractCalls as any,
    allowFailure: true,
    query: {
      ...PORTFOLIO_REFETCH_POLICY,
      enabled: userAccount && userRewardVaultFarms.length > 0 && options?.enabled !== false,
    },
  })

  const userFarmPositions: FarmPosition[] = useMemo(() => {
    if (!allUserFarms.length || (!kodiakStakingResults && (!rewardVaultBalanceResults || !rewardVaultEarnedResults))) {
      return []
    }

    const farmPositions: FarmPosition[] = []

    // Process Kodiak farms
    if (kodiakStakingResults && userKodiakFarms.length > 0) {
      userKodiakFarms.forEach((farm, index) => {
        const stakingResult = kodiakStakingResults[index]
        if (stakingResult.status === 'success' && stakingResult.result) {
          const positions = stakingResult.result as KodiakFarmStakedPosition[]

          // Sum up all liquidity from locked positions
          const totalLiquidity = positions.reduce((sum, position) => sum + position.liquidity, BigInt(0))

          if (totalLiquidity > BigInt(0)) {
            const vault = userVaultsWithFarms.find((v) => v.farm?.id === farm.id)
            if (vault && vault.tokenLp) {
              const formattedStakedAmount = formatUnits(totalLiquidity, vault.tokenLp.decimals)
              const stakedAmountUSD = Number(formattedStakedAmount) * (vault.tokenLp.price || 0)

              farmPositions.push({
                farm,
                vault,
                stakedAmount: totalLiquidity.toString(),
                formattedStakedAmount,
                stakedAmountUSD,
                claimableRewards: [],
                claimableRewardsUSD: 0,
                apr: farm.apr,
                provider: 'kodiak',
              })
            }
          }
        }
      })
    }

    // Process Reward Vault farms. The backend provider discriminator is currently "bgt".
    if (rewardVaultBalanceResults && rewardVaultEarnedResults && userRewardVaultFarms.length > 0) {
      userRewardVaultFarms.forEach((farm, index) => {
        const balanceResult = rewardVaultBalanceResults[index]
        const earnedResult = rewardVaultEarnedResults[index]

        if (balanceResult?.status === 'success' && balanceResult.result) {
          const stakedAmount = balanceResult.result as bigint

          if (stakedAmount > BigInt(0)) {
            const vault = userVaultsWithFarms.find((v) => v.farm?.id === farm.id)
            if (vault && vault.tokenLp) {
              const formattedStakedAmount = formatUnits(stakedAmount, vault.tokenLp.decimals)
              const stakedAmountUSD = Number(formattedStakedAmount) * (vault.tokenLp.price || 0)

              // Process claimable reward vault rewards
              const claimableRewards: RewardToken[] = []
              let claimableRewardsUSD = 0

              if (earnedResult?.status === 'success' && earnedResult.result) {
                const earnedAmount = earnedResult.result as bigint
                if (earnedAmount > BigInt(0) && farm.rewardTokens.length > 0) {
                  const rewardToken = farm.rewardTokens[0]
                  const formattedEarned = formatUnits(earnedAmount, rewardToken.decimals)
                  const earnedUSD = Number(formattedEarned) * (rewardToken.price || 0)

                  claimableRewards.push({
                    token: rewardToken,
                    amount: earnedAmount.toString(),
                    amountUSD: earnedUSD,
                  })
                  claimableRewardsUSD += earnedUSD
                }
              }

              farmPositions.push({
                farm,
                vault,
                stakedAmount: stakedAmount.toString(),
                formattedStakedAmount,
                stakedAmountUSD,
                claimableRewards,
                claimableRewardsUSD,
                apr: farm.apr,
                provider: 'bgt',
              })
            }
          }
        }
      })
    }

    return farmPositions
  }, [
    allUserFarms,
    kodiakStakingResults,
    rewardVaultBalanceResults,
    rewardVaultEarnedResults,
    userKodiakFarms,
    userRewardVaultFarms,
    userVaultsWithFarms,
  ])

  const refetchFarmPositions = useCallback(async () => {
    const refetchTasks: Promise<unknown>[] = []

    if (userAccount && userKodiakFarms.length > 0) {
      refetchTasks.push(refetchKodiakStakesData())
    }

    if (userAccount && userRewardVaultFarms.length > 0) {
      refetchTasks.push(refetchRewardVaultBalanceData(), refetchRewardVaultEarnedData())
    }

    if (refetchTasks.length === 0) return
    await Promise.allSettled(refetchTasks)
  }, [
    refetchKodiakStakesData,
    refetchRewardVaultBalanceData,
    refetchRewardVaultEarnedData,
    userAccount,
    userKodiakFarms.length,
    userRewardVaultFarms.length,
  ])

  const refetchFarmRewards = useCallback(async () => {
    if (!(userAccount && userRewardVaultFarms.length > 0)) return
    await refetchRewardVaultEarnedData()
  }, [refetchRewardVaultEarnedData, userAccount, userRewardVaultFarms.length])

  return {
    ...queryResult,
    data: userFarmPositions,
    isLoading:
      queryResult.isLoading || isLoadingStakesData || isLoadingRewardVaultBalances || isLoadingRewardVaultEarned,
    error: queryResult.error || errorStakesData || errorRewardVaultBalances || errorRewardVaultEarned,
    refetchFarmPositions,
    refetchFarmRewards,
  }
}
